<template>
  <BExpansionPanel
    expand
  >
    <template #header>
      <div class="block-title">
        <div class="title-text mr-100">
          {{ $t('productCategory.title') }}
        </div>
      </div>
    </template>
    <template #additional-header-content>
      <div class="block-title">
        <BBtn
          size="small"
          fab
          flat
          :disabled="isAddButtonDisabled"
          @click="openAddModal"
        >
          <BIcon size="small">
            add
          </BIcon>
        </BBtn>
      </div>
    </template>
    <div class="block-content">
      <VWait
        class="full-width"
        for="none"
        :visible="loading"
      >
        <template #waiting>
          <BContentLoader
            :height="32"
            :width="230"
          >
            <rect
              x="0"
              y="8.5"
              rx="6"
              ry="6"
              width="160"
              height="12"
            />
          </BContentLoader>
        </template>
        <div
          v-for="linkedCategory in associations"
          :key="linkedCategory.id"
          class="category-item"
        >
          <div class="category-header">
            <span class="category-name">{{ getCategoryName(linkedCategory.productCategoryId) }}</span>
            <BBtn
              size="small"
              fab
              flat
              data-test="delete"
              @click="() => handleDelete(linkedCategory.id)"
            >
              <BIcon size="small">
                delete
              </BIcon>
            </BBtn>
          </div>
          <div class="category-field">
            <label class="inline-label">{{ $t('productCategoryAssociation.status') }}</label>
            <BSelect
              :model-value="linkedCategory.productCategoryStatusId"
              class="status-select"
              :items="productCategoryStatuses"
              item-text="name"
              item-value="id"
              @change="handleStatusChange(linkedCategory.id, $event)"
            />
          </div>
          <div class="category-field">
            <label class="inline-label">{{ $t('productCategoryAssociation.product') }}</label>
            <div class="product-tags-row">
              <div class="product-tags-wrapper">
                <BLabel
                  v-for="productAssoc in linkedCategory.callTargetProductAssociations"
                  :key="productAssoc.id"
                  round
                  class="product-tag"
                >
                  <span
                    class="product-tag-text"
                    :title="getProductName(productAssoc.productId, linkedCategory.productCategoryId)"
                  >{{ getProductName(productAssoc.productId, linkedCategory.productCategoryId) }}</span>
                </BLabel>
                <span
                  v-if="linkedCategory.callTargetProductAssociations.length === 0"
                  class="product-tag-unset"
                >{{ $t('productCategoryAssociation.productUnset') }}</span>
              </div>
              <BSelectMenu
                :candidates="getProductsForCategory(linkedCategory.productCategoryId)"
                :selected-ids="getLinkedProductIds(linkedCategory)"
                @click-append="(productId) => handleProductAppend(linkedCategory, productId)"
                @click-delete="(productId) => handleProductDelete(linkedCategory, productId)"
              />
            </div>
          </div>
        </div>
      </VWait>
    </div>
  </BExpansionPanel>
  <ProductCategoryAssociationAddModal
    class="no-section-spacing"
    :modal-name="addModalName"
    :product-categories="unlinkedCategories"
    :product-category-statuses="productCategoryStatuses"
    :submitting="addSubmitting"
    @add="handleAdd"
  />
</template>

<script lang="ts" setup>
import { ref, inject, toRef, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirm } from '@/composable/message-dialog';
import { useProductCategoryAssociation, type ProductCategoryAssociation } from '@/composable/user/lead/product-category-association';
import { KEY_OF_MODAL, KEY_OF_TOAST } from '@/injection-keys';
import ProductCategoryAssociationAddModal from './ProductCategoryAssociationAddModal.vue';

const props = defineProps<{
  callTargetId: number;
}>();

const { t } = useI18n();
const modal = inject(KEY_OF_MODAL);
const toast = inject(KEY_OF_TOAST);

const addModalName = 'addProductCategoryAssociation';
const addSubmitting = ref(false);
const { confirm } = useConfirm();

const {
  associations,
  productCategoryStatuses,
  loading,
  unlinkedCategories,
  isAddButtonDisabled,
  getCategoryName,
  getProductsForCategory,
  getLinkedProductIds,
  getProductName,
  fetchAll,
  addAssociation,
  updateStatus,
  addProductAssociation,
  removeProductAssociation,
  deleteAssociation,
} = useProductCategoryAssociation(toRef(props, 'callTargetId'));

onMounted(() => {
  fetchAll();
});

const openAddModal = () => {
  modal.show(addModalName);
};

const handleAdd = async (productCategoryId: number, productCategoryStatusId: number) => {
  addSubmitting.value = true;
  try {
    await addAssociation(productCategoryId, productCategoryStatusId);
    toast.show({ message: t('productCategoryAssociation.addSuccess'), type: 'success' });
    modal.hide(addModalName);
  } catch {
    toast.show({ message: t('productCategoryAssociation.addFail'), type: 'error' });
  } finally {
    addSubmitting.value = false;
  }
};

const handleStatusChange = async (associationId: number, productCategoryStatusId: number) => {
  try {
    await updateStatus(associationId, productCategoryStatusId);
    toast.show({ message: t('productCategoryAssociation.updateSuccess'), type: 'success' });
  } catch {
    toast.show({ message: t('productCategoryAssociation.updateFail'), type: 'error' });
  }
};

const handleProductAppend = async (association: ProductCategoryAssociation, productId: number) => {
  try {
    await addProductAssociation(association.id, productId);
    toast.show({ message: t('productCategoryAssociation.addProductSuccess'), type: 'success' });
  } catch {
    toast.show({ message: t('productCategoryAssociation.addProductFail'), type: 'error' });
  }
};

const handleProductDelete = async (association: ProductCategoryAssociation, productId: number) => {
  try {
    await removeProductAssociation(association, productId);
    toast.show({ message: t('productCategoryAssociation.removeProductSuccess'), type: 'success' });
  } catch {
    toast.show({ message: t('productCategoryAssociation.removeProductFail'), type: 'error' });
  }
};

const handleDelete = async (associationId: number) => {
  const ok = await confirm({
    message: t('productCategoryAssociation.deleteConfirmMessage'),
  });
  if (!ok) return;

  try {
    await deleteAssociation(associationId);
    toast.show({ message: t('productCategoryAssociation.deleteSuccess'), type: 'success' });
  } catch {
    toast.show({ message: t('productCategoryAssociation.deleteFail'), type: 'error' });
  }
};
</script>

<style lang="scss" scoped>
.block-title {
  display: flex;
  align-items: center;
  width: 100%;
  font-weight: 600;
}

.full-width {
  width: 100%;
}

.category-item {
  padding: $basespace-200 0;

  &:not(:last-child) {
    border-bottom: 1px solid $bdcolor-base;
  }
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: $basespace-200;
}

.category-name {
  font-weight: 600;
  font-size: $fontsize-200;
}


.inline-label {
  display: block;
  font-size: $fontsize-100;
  color: $textcolor-light;
  margin-bottom: $basespace-50;
}

.status-select {
  width: 100%;
}

.category-field + .category-field {
  margin-top: $basespace-200;
}

.product-tags-row {
  display: flex;
  align-items: flex-start;
}

.product-tags-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: $basespace-50;
  flex: 1;
  min-width: 0;
}

.product-tag {
  max-width: 100%;
}

.product-tag-text {
  @include m-truncate-text;
  width: 100%;
}

.product-tag-unset {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 $basespace-200;
  font-size: $fontsize-100;
  color: $textcolor-light;
  border: 1px dashed $bdcolor-base;
  border-radius: 14px;
}
</style>
